import { Context } from "../../../core/impl/Context";
import { CountryDetectionConstants } from "../../../countrydetection/impl/enums/CountryDetectionConstants";
import { NetworkInstance } from "./../model/NetworkInstance";
import { getLogger } from "@goodgamestudios/logger";
import { Country } from "../../../countrydetection/impl/model/Country";

const logger = getLogger("WorldAssignment.NetworkInstancesManager");


export class NetworkInstancesManager {
    /**
     * This is the result of the WorldAssignment Logic run at startup
     */

    private _selectedNetworkInstance: NetworkInstance;
    private _networkInstances: NetworkInstance[];
    private _initialized: boolean;

    public initializeInstances(instances: NetworkInstance[]) {
        this._networkInstances = instances;
        this._initialized = true;
    }

    public get networkInstances(): NetworkInstance[] {
        return this._networkInstances;
    }

    public get selectedNetworkInstance(): NetworkInstance {
        return this._selectedNetworkInstance;
    }


    public getInstanceById(id: number): NetworkInstance {
        const findByID = (item: NetworkInstance) => item.instanceId === id;
        return this.networkInstances.find(findByID);
    }


    public getInstanceByZoneId(zoneId: number): NetworkInstance {
        const findByZoneId = (item: NetworkInstance) => item.zoneId === zoneId;
        return this.networkInstances.find(findByZoneId);
    }

    public getInstancesByCountryCode(countryCode: string): NetworkInstance[] {
        let instances: NetworkInstance[] = [];
        let instance: NetworkInstance;

        for (let i = 0; i < this.networkInstances.length; i++) {
            instance = this.networkInstances[i];
            for (let c = 0; c < instance.countries.length; c++) {
                if (instance.countries[c].ggsCountryCode === countryCode) {
                    instances.push(instance);
                    break;
                }
            }
        }
        return instances;
    }

    /***
     * Run the main logic of WorldAssignment against the available Instances for the countryCode
     * @param coutryCode
     */
    public assignNetworkInstance(coutryCode: string): NetworkInstance {

        if (!this._initialized) {
            logger.debug("NetworkInstancesManager not initialized. Can not assignNetworkInstance.");
            return;
        }

        logger.debug("select network instance - given country code:", coutryCode);


        let instance: NetworkInstance;

        if (Context.instance.globals.presetInstanceId > 0) {
            instance = this._networkInstances.find(finderById(Context.instance.globals.presetInstanceId));
            if (instance) {
                logger.debug("NetworkInstancesManager.assignNetworkInstance -->presetInstanceId: ", Context.instance.globals.presetInstanceId);
                this._selectedNetworkInstance = instance;
                return;
            }
        }

        if (Context.instance.globals.zoneIdFromFriendInviteCode > 0) {
            instance = this.getInstanceByZoneId(Context.instance.globals.zoneIdFromFriendInviteCode);

            if (instance) {
                logger.debug("NetworkInstancesManager.assignNetworkInstance -->friendInviteZoneId: ", Context.instance.globals.zoneIdFromFriendInviteCode);
                this._selectedNetworkInstance = instance;
                return;
            }
        }

        if (Context.instance.globals.instanceIdFromNetworkCookie > 0) {
            instance = this.getInstanceById(Context.instance.globals.instanceIdFromNetworkCookie);

            if (instance) {
                logger.debug("NetworkInstancesManager.assignNetworkInstance -->instanceIdByNetworkCookie: ", Context.instance.globals.instanceIdFromNetworkCookie);
                this._selectedNetworkInstance = instance;
                return;
            }
        }

        if (coutryCode !== CountryDetectionConstants.INSTANCE_COUNTRY_DEFAULT) {

            instance = findPreferredInstanceForCountry(this._networkInstances, coutryCode);

            if (instance) {
                logger.debug("NetworkInstancesManager.assignNetworkInstance  --> coutryCode: ", coutryCode);
                this._selectedNetworkInstance = instance;
                return;
            }
        }

        if (Context.instance.networkSettings.defaultInstanceId > 0) {
            instance = this.getInstanceById(Context.instance.networkSettings.defaultInstanceId);
            if (instance) {
                logger.debug("NetworkInstancesManager.assignNetworkInstance --> networkSettings.defaultInstanceId : ", Context.instance.networkSettings.defaultInstanceId);
                this._selectedNetworkInstance = instance;
                return;
            }
        }

        logger.debug("NetworkInstancesManager.assignNetworkInstance --> fallback to first instance in list: ", this.networkInstances[0]);

        this._selectedNetworkInstance = this.networkInstances[0];
        return this._selectedNetworkInstance;
    }


    public forceInstance(instance: NetworkInstance) {
        // TODO HTML5 should we somehow validate if instance being forced is compatible with Country and Network xmls?
        this._selectedNetworkInstance = instance;
    }

    public forceInstanceByCountry(countryCode: string): NetworkInstance {
        const instance = findPreferredInstanceForCountry(this.networkInstances, countryCode);
        if (!instance) {
            logger.error("forceInstanceByCountry: could not find any instance for ", countryCode)
        }
        else {
            this.forceInstance(instance);
        }
        return this._selectedNetworkInstance;
    }


}

export const getInstancesByCountryCode = (networkInstances: NetworkInstance[], countryCode: string): NetworkInstance[] => {
    const hasValidCountryCode = (country: Country): boolean => country.ggsCountryCode === countryCode;
    const byCountryCode = (instance: NetworkInstance) => instance.countries.some(hasValidCountryCode);
    return networkInstances.filter(byCountryCode);
}

//const internationalForCountryCode=(cc: string) => (item: NetworkInstance) => item.instanceId === id;


// this is the original function - and it does not make much sense..   why comparing a random num against the ratio and use that index only if below the ratio  (btw if random value is higher than the ratio there is a chance of getting undefined
const randomizeInstancesByRatios = (instances: NetworkInstance[], ratios: number[]) => {
    for (let i: number = 0; i < ratios.length; i++) {
        if (Math.random() < ratios[i]) {
            return instances[i];
        }
    }
    return undefined;
}

export const finderById = (id: number) => (item: NetworkInstance) => item.instanceId === id;


/**
 * Calculates the capacity ratio for the available instances and picks one randomly taking the ratio weight into account
 * @param list
 * @returns {NetworkInstance}
 */
const getInstanceByCapacityRatio = (list: NetworkInstance[]): NetworkInstance => {
    // FIXME TODO HTML5  logic of instance choice based on ratio is broken.   but BF is not using it and EM seems to mostly rely on isFavorite property
    //let capacitiesRatio: number[] = getCapacityRatio(getRemainingCapacityForInstances(notInternational).map(sanitize));
    //return randomizeInstancesByRatios(notInternational, capacitiesRatio);
    return list && list.length > 0 ? list[0] : undefined;
}

/**
 * Searches for the Instance marked as Favorite otherwise returns an instance based on Capacity ratio
 * @param list
 */
const getFavoriteInstance = (list: NetworkInstance[]): NetworkInstance => list.find(instance => instance.isFavorite) || getInstanceByCapacityRatio(list);

export const findPreferredInstanceForCountry = (instances: NetworkInstance[], countryCode: string): NetworkInstance => {
    const filteredByCC = getInstancesByCountryCode(instances, countryCode);
    let instance = getFavoriteInstance(filteredByCC.filter(instance => !instance.isInternational));
    return instance || getFavoriteInstance(filteredByCC.filter(instance => instance.isInternational));

    /*   OLD IMPLEMENTATION
     logger.debug("NetworkInstancesManager.getPreferredInstanceForCountry: ", countryCode);
     let instance: NetworkInstance;
     if (instances) {
     instance = filteredByCC[0];
     if (!instance.isFavorite) {
     const filteredByFavorite = getUnfavoredInstanceSameTypeAs(filteredByCC, instance);
     let capacitiesRatio: number[] = CapacityUtils.getCapacityRatio(CapacityUtils.getRemainingCapacityForInstances(filteredByFavorite));
     for (let i: number = 0; i < capacitiesRatio.length; i++) {
     if (Math.random() < capacitiesRatio[i]) {
     instance = filteredByFavorite[i];
     break;
     }
     }
     }
     } else {

     //  instance = instances.find(finderById(Context.instance.networkSettings.defaultInstanceId))
     instance = this.getInstanceById(Context.instance.networkSettings.defaultInstanceId);
     logger.debug("NetworkInstancesManager.getPreferredInstanceForCountry get instance by defaultInstanceId: ", Context.instance.networkSettings.defaultInstanceId);
     }

     if (!instance) {
     logger.debug("NetworkInstancesManager.getPreferredInstanceForCountry: Wrong configured network.xml: We can't find any instance");
     }

     return instance;*/
}
